import { css } from '@emotion/core'
import { useFeatureFlags } from '@fs/zion-flags'
import { Arrow, ArrowCaret, ControlHide, ControlSettingsAdjust, ControlView, MenuFilter } from '@fs/zion-icon'
import { useLocation, useQueryParams } from '@fs/zion-router'
import { getImage, useSls, useSlsActions, useSlsDispatchContext, useSlsStateContext } from '@fs/zion-sls'
import {
  Bleed,
  Button,
  Cell,
  colors,
  Divider,
  ExpandableCard,
  FlowGrid,
  Grid,
  H6,
  IconButton,
  LayoutBand,
  MarginBox,
  Paragraph,
  RecordBlock,
  ResponsiveTable,
  ResponsiveTableRow,
  Row,
  Separator,
  Skeleton,
  Theme,
  useOverlay,
} from '@fs/zion-ui'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSharedBrowserStateContext } from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2DispatchContext, useR2StateContext } from '../../../providers/r2Provider/R2Context'
import { headers } from '../../../../shared/services/config'
import { getImageThumbUrl } from '../../../../shared/services/das/dasService'

import useRecords from '../../indexing/hooks/useRecords'
import {
  useHistoryImageData,
  usePersonSelector,
  useHistoryFilters,
  useDeletedPersons,
  useSortedPersons,
} from '../hooks'
import DeletedPersons from './DeletedPersons'
import FiltersPanel, { ALL_CHANGES_FILTER } from './panels/FiltersPanel'
import PreferencesPanel, { VIEW_FORMAT } from './panels/PreferencesPanel'
import PersonChangesHistory from './PersonChangesHistory'
import PersonRecentChanges from './PersonRecentChanges'

const HistoryViewer = () => {
  const { t, i18n } = useTranslation()
  const filterSheetOverlay = useOverlay()
  const preferencesSheetOverlay = useOverlay()
  const { query, removeQuery } = useQueryParams()

  const location = useLocation()
  const state = useSlsStateContext()
  const { currentImageArk } = state
  const dispatch = useSlsDispatchContext()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const { shared_dasProxy, rip_sharedImageViewer_showJiapu: showJiapuDataFlag } = useFeatureFlags([
    'shared_dasProxy',
    'rip_sharedImageViewer_showJiapu',
  ])
  const { thumbnails } = useSharedBrowserStateContext()

  const dpsRef = useRef()

  // Derived state using zion-sls utilities
  const currentImage = useMemo(
    () => getImage(state?.images, state?.currentImageArk) || {},
    [state?.images, state?.currentImageArk]
  )
  const personIdsOrderForImage = state.personOrder?.find((po) => po.partitionKey === currentImageArk)

  const { allRecords } = useRecords({
    images: state?.images,
    currentImageArk: state?.currentImageArk,
    personIdsOrder: personIdsOrderForImage?.personIds,
    thumbnails,
  })

  const imageArk = location.pathname.split('/').pop()
  const { loading, data } = useSls({
    imageArks: [imageArk],
    fsUserAgentChain: headers['FS-User-Agent-Chain'],
    dispatch: true,
    skipProcessingLargeData: !showJiapuDataFlag.isOn,
  })
  const { types: slsTypes } = data

  const { loading: actionsLoading } = useSlsActions({
    imageArks: [imageArk],
    fsUserAgentChain: headers['FS-User-Agent-Chain'],
    dispatch: true,
    enableHook: Boolean(!loading && currentImage?.partitionKey && !currentImage?.actions?.length > 0),
  })

  // Custom hooks to replace complex useEffects
  const { imageData, collection } = useHistoryImageData(imageArk, loading, state)
  const sortedPersons = useSortedPersons(currentImage, loading)
  const deletedPersonActions = useDeletedPersons(currentImage, loading, i18n)

  // Local state
  const [expanded, setExpanded] = useState(true)
  const [fieldFilter, setFieldFilter] = useState({
    value: ALL_CHANGES_FILTER,
    label: t('history-viewer.filter.all-changes', 'All Changes'),
  })
  const [showDeletedNames, setShowDeletedNames] = useState(false)

  // Person selection and derived data
  const { selectedPerson, genderValue, relationshipType, personName, personTitle, selectPerson } = usePersonSelector(
    currentImage,
    state,
    allRecords,
    query.personArk,
    r2Dispatch,
    t
  )

  // History filtering
  const { filteredHistoryFields, filterTitle } = useHistoryFilters(
    currentImage,
    selectedPerson,
    fieldFilter,
    loading,
    query.personArk,
    personName,
    t
  )

  // Derived display values
  const isIndividualName = Boolean(query.personArk)
  const title = isIndividualName ? personTitle : filterTitle
  const titleDisplayHeader = isIndividualName ? personName : ''

  // Parse image name helper
  const parseImageName = useCallback(() => {
    const nameParts = imageData?.name?.split('_')
    if (nameParts && nameParts[1]) {
      return parseInt(nameParts[1], 10)
    }
    return '?'
  }, [imageData?.name])

  useEffect(() => {
    dispatch({
      type: 'SET_DATA',
      value: {
        currentImageArk: imageArk,
      },
    })
  }, [dispatch, imageArk])

  const seeAll = useCallback(() => {
    removeQuery('personArk')
    const closeOverlay = (overlay) => {
      if (overlay.isOpen) {
        overlay.close()
      }
    }
    ;[filterSheetOverlay, preferencesSheetOverlay].forEach(closeOverlay)
  }, [filterSheetOverlay, preferencesSheetOverlay, removeQuery])

  const headingDisplay = useMemo(() => {
    return title === t('history-viewer.heading', 'All Changes for Image')
      ? t('history-viewer.image-name', 'Image {imageName}', { imageName: parseImageName() })
      : titleDisplayHeader
  }, [title, titleDisplayHeader, parseImageName, t])

  const getheaderBottomLineColor = useCallback(() => {
    if (genderValue === 'male') return colors.blue80
    if (genderValue === 'female') return colors.red80
    return colors.gray10
  }, [genderValue])

  const headerCss = useMemo(
    () => css`
      border-bottom: 5px solid ${getheaderBottomLineColor()};
    `,
    [getheaderBottomLineColor]
  )

  const shouldShowCollapseButton = r2State.historyFormat === VIEW_FORMAT.group

  return (
    <>
      <Theme theme="day">
        <LayoutBand css={headerCss}>
          <Row gutters="nano" alignY="middle">
            <Button
              data-testid="back-to-record-button"
              Icon={Arrow}
              iconDirection="backward"
              to={`${location.pathname}?view=index`}
            >
              {t('history-viewer.back.button', 'Back to Record')}
            </Button>
            <IconButton title="Breadcrumb Caret" Icon={ArrowCaret} size="md" disabled />
            {isIndividualName ? (
              <Button data-testid="view-all-name-button" onClick={seeAll}>
                {t('history-viewer.view-all-names.button', 'View All Names')}
              </Button>
            ) : (
              <Paragraph size="sm">{t('history-viewer.view-all-names.button', 'View All Names')}</Paragraph>
            )}
            {isIndividualName && !loading ? (
              <IconButton title="Breadcrumb Caret" Icon={ArrowCaret} size="md" disabled />
            ) : null}
            {isIndividualName && !loading ? <Paragraph size="sm">{titleDisplayHeader}</Paragraph> : null}
          </Row>
          <Separator />
          <Row alignY="bottom">
            {collection && collection.collections?.[0]?.title ? (
              <RecordBlock
                name={headingDisplay}
                personRole={relationshipType}
                details={collection && collection.collections?.[0]?.title}
                thumbnail={getImageThumbUrl(imageArk, shared_dasProxy.isOn)}
                size="lg"
                growX
              />
            ) : (
              <Skeleton.RecordBlock growX />
            )}
            {isIndividualName && (
              <Button
                data-testid="history-preferences-button"
                Icon={ControlSettingsAdjust}
                disabled={!query.personArk || preferencesSheetOverlay.isOpen}
                onClick={preferencesSheetOverlay.handleClick}
              >
                {t('history-viewer.preferences.button', 'Preferences')}
              </Button>
            )}
            {isIndividualName && (
              <Button
                data-testid="filter-history-button"
                Icon={MenuFilter}
                disabled={!query.personArk || filterSheetOverlay.isOpen}
                onClick={filterSheetOverlay.handleClick}
              >
                {t('history-viewer.filter.button', 'Filter')}
              </Button>
            )}
          </Row>
          {!isIndividualName && <Separator />}
          {!isIndividualName && (
            <Button
              onClick={() => setShowDeletedNames(!showDeletedNames)}
              Icon={showDeletedNames ? ControlHide : ControlView}
            >
              {showDeletedNames
                ? t('history-viewer.hide-deleted-names.button', 'HIDE DELETED NAMES')
                : t('history-viewer.show-deleted-names.button', 'SHOW DELETED NAMES')}
            </Button>
          )}
          <Separator />
        </LayoutBand>
      </Theme>
      <Divider />
      <LayoutBand marginY="sm">
        <Grid>
          <Cell columns={6} alignY="middle">
            <Row>
              {shouldShowCollapseButton && (
                <Button data-testid="expand-history-button" onClick={() => setExpanded((prev) => !prev)}>
                  {expanded
                    ? t('history-viewer.collapse.button', 'Collapse All')
                    : t('history-viewer.expand.button', 'Expand All')}
                </Button>
              )}
            </Row>
          </Cell>
        </Grid>

        {!loading && !actionsLoading && !query.personArk && showDeletedNames && (
          <div ref={dpsRef}>
            <Separator size="sm" />
            <DeletedPersons
              deletedPersonActions={deletedPersonActions}
              backgroundColor={colors.red02}
              expanded={expanded}
              onToggle={setExpanded}
            />
            <Separator size="sm" />
          </div>
        )}

        {!loading &&
          !actionsLoading &&
          !query.personArk &&
          sortedPersons.map((person) => (
            <div key={person.id}>
              <PersonRecentChanges expanded={expanded} person={person} selectPerson={selectPerson} />
              <Separator size="sm" />
            </div>
          ))}
        {(loading || actionsLoading) && query.personArk && <HistoryLoadingSkeleton t={t} />}
        {!loading && !actionsLoading && query.personArk && (
          <PersonChangesHistory
            filter={fieldFilter}
            historyChanges={filteredHistoryFields}
            expanded={expanded}
            title={title}
            person={selectedPerson}
          />
        )}

        <FiltersPanel slsTypes={slsTypes} filterSheetOverlay={filterSheetOverlay} onChange={setFieldFilter} />
        <PreferencesPanel overlayProps={preferencesSheetOverlay} />
      </LayoutBand>
    </>
  )
}

// Loading skeleton for history cards
const HistoryLoadingSkeleton = ({ t }) => (
  <div
    css={css`
      margin-top: 1.875rem;
    `}
  >
    <FlowGrid orientation="vertical">
      {[1, 2, 3].map((i) => (
        <ExpandableCard
          key={i}
          title={
            <H6>
              <Skeleton.Paragraph size="sm" maxWidth="40%" />
            </H6>
          }
          open
          setOpen={() => {}}
        >
          <Bleed top>
            <MarginBox marginX="sm">
              <ResponsiveTable
                totalColumnCount={3}
                atSizeColumns={{
                  default: {
                    labels: [
                      t('history-viewer.changes-table.change.label', 'Change'),
                      t('history-viewer.changes-table.value.label', 'Value'),
                      t('history-viewer.changes-table.date.label', 'Date'),
                    ],
                    columnWidths: [3, 6, 3],
                  },
                }}
              >
                {[1, 2, 3, 4].map((rowIndex) => (
                  <ResponsiveTableRow
                    key={rowIndex}
                    column1={<Skeleton.Paragraph size="sm" maxWidth="80%" />}
                    column2={<Skeleton.Paragraph size="sm" maxWidth="90%" />}
                    column3={<Skeleton.Paragraph size="sm" maxWidth="85%" />}
                  />
                ))}
              </ResponsiveTable>
              <Separator size="sm" />
            </MarginBox>
          </Bleed>
        </ExpandableCard>
      ))}
    </FlowGrid>
  </div>
)

export default HistoryViewer
